import React, { FC, useEffect, useState } from 'react';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as HelpIcon } from 'assets/icons/help.svg';
import { Dropdown, DropdownList } from '@cokitchen/cokitchen-components';
import { ArrowDownIcon } from 'cokitchen-icons';
import CreateMetadata from './create-metadata';
import { MetadataItemType } from 'pages/metadata/types';
import MetadataTag from 'components/base/metadata-tag';

type MetadataDropdownProps = {
  title: string;
  description: string;
  onCreate: (e: any, popular: boolean) => void;
  metadataList: MetadataItemType[];
  selectedMetadata: MetadataItemType[];
  onTagClick: (item: MetadataItemType) => void;
};

const MetadataDropdown: FC<MetadataDropdownProps> = ({
  title,
  onCreate,
  selectedMetadata,
  description,
  metadataList,
  onTagClick
}) => {
  const titleToLower = title.toLowerCase();

  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredList, setFilteredList] = useState(metadataList);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    const { selectedItems, unSelectedItems } = metadataList.reduce(
      (acc: { selectedItems: string[]; unSelectedItems: MetadataItemType[] }, item) => {
        if (selectedMetadata?.find((m) => m.id === item.id)) {
          acc?.selectedItems.push(item.id);
        } else acc?.unSelectedItems?.push(item);
        return acc;
      },
      {
        selectedItems: [],
        unSelectedItems: []
      }
    );
    setSelected(selectedItems);
    setFilteredList(unSelectedItems);
  }, [selectedMetadata, metadataList]);

  return (
    <div className='py-2'>
      <div className='space-y-4'>
        <p className='flex items-center gap-1 text-sm font-medium capitalize'>
          {titleToLower} <HelpIcon className='text-gray-4' />
        </p>

        <Dropdown
          popperOptions={{
            modifiers: [
              {
                name: 'sameWidth',
                enabled: true,
                fn: ({ instance, state }) => {
                  const triggerReferenceWidth = `${state.rects.reference.width}px`;
                  if (state.styles.popper.width !== triggerReferenceWidth) {
                    state.styles.popper.width = triggerReferenceWidth;
                    // force update the popper instance, so it can reposition the dropdown properly
                    instance.update();
                  }
                },
                phase: 'beforeWrite',
                requires: ['computeStyles'],
                effect: ({ state }) => {
                  state.elements.popper.style.width = `${
                    state.elements.reference.getBoundingClientRect().width
                  }px`;
                }
              }
            ]
          }}
          visible={showDropdown}
          onClickOutside={() => setShowDropdown(false)}
          placement='bottom-end'
          content={
            <div
              className='px-5 py-3 flex items-center gap-4 border border-[#E6E5E5] rounded-[6px]'
              onClick={() => setShowDropdown(!showDropdown)}>
              <div className='grow'>
                {selected.length === 0 ? (
                  <p className='text-sm text-gray-4'>Select {titleToLower}</p>
                ) : (
                  <div
                    className='flex flex-wrap gap-3'
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDropdown(false);
                    }}>
                    {selectedMetadata.map((data, i) => (
                      <MetadataTag
                        color='red'
                        name={data?.name}
                        key={i}
                        onDelete={() => onTagClick(data)}
                        hasDeleteBtn={true}
                      />
                    ))}
                  </div>
                )}
              </div>
              <ArrowDownIcon className='text-gray-8 min-w-[20px]' />
            </div>
          }>
          <DropdownList className='p-4 max-h-[500px]'>
            <div className='w-full'>
              <p className='pb-3 border-b border-[#DDDEDF] text-xs flex items-center gap-2 text-[#85806F]'>
                <InfoIcon className='text-[#BD8F05]' /> {description}
              </p>

              <div className='flex flex-wrap gap-3 py-5 max-h-[200px] overflow-y-auto'>
                {filteredList.map((data) => (
                  <MetadataTag
                    color='gray'
                    name={data?.name}
                    key={data.id}
                    onClick={() => onTagClick(data)}
                  />
                ))}
              </div>
              <div className='rounded-[6px] bg-[#F7F7F7] pt-1'>
                <CreateMetadata onEnter={onCreate} />
              </div>
            </div>
          </DropdownList>
        </Dropdown>
      </div>
    </div>
  );
};

export default MetadataDropdown;
